/* Copyright (c) 2025 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { KeyboardEvent } from "react";
import { When } from "react-if";
import styled from "styled-components";
import { Description, Input, theme, FlexContainer } from "@nordcloud/gnui";

const NumberContainer = styled.div`
  margin-right: ${theme.spacing.spacing02};
  width: 10rem;
`;

type Props = {
  from?: number;
  to?: number;
  onChangeFrom: (input: string) => void;
  onChangeTo: (input: string) => void;
};

export function NumberRangeInput({
  from,
  to,
  onChangeFrom,
  onChangeTo,
}: Props) {
  const { isNumberToInvalid, isNumberFromNegative, isNumberToNegative } =
    useNumberHook(from, to);

  return (
    <FlexContainer direction="column">
      <FlexContainer>
        <NumberContainer>
          <Input
            type="number"
            min="0"
            max={Number.MAX_SAFE_INTEGER}
            placeholder="From"
            status={isNumberFromNegative ? "danger" : undefined}
            value={from ?? ""}
            showClearButton={from !== undefined}
            onChange={(event) => onChangeFrom(event.target.value)}
            onClear={() => onChangeFrom("")}
            onKeyDown={handleKeyPress}
          />
        </NumberContainer>
        <NumberContainer>
          <Input
            type="number"
            min="0"
            max={Number.MAX_SAFE_INTEGER}
            placeholder="To"
            status={
              isNumberToInvalid || isNumberToNegative ? "danger" : undefined
            }
            value={to ?? ""}
            showClearButton={to !== undefined}
            onChange={(event) => onChangeTo(event.target.value)}
            onClear={() => onChangeTo("")}
            onKeyDown={handleKeyPress}
          />
        </NumberContainer>
      </FlexContainer>
      <When condition={isNumberToInvalid}>
        <Description>
          Value &quot;To&quot; must be bigger than &quot;From&quot;.
        </Description>
      </When>
      <When condition={isNumberFromNegative || isNumberToNegative}>
        <Description>Value must be bigger than 0.</Description>
      </When>
    </FlexContainer>
  );
}

const exceptSymbols = ["e", "E"];

const handleKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
  if (exceptSymbols.includes(event.key)) {
    return event.preventDefault();
  }
};

function useNumberHook(numberFrom?: number, numberTo?: number) {
  const from = numberFrom ?? 0;
  const to = numberTo ?? Number.POSITIVE_INFINITY;

  const hasNumbers =
    (Boolean(from) || numberFrom === 0) && (Boolean(to) || numberTo === 0);
  const isNumberToInvalid = hasNumbers && from > to;

  const isNumberFromNegative = numberFrom ? numberFrom < 0 : false;
  const isNumberToNegative = numberTo ? numberTo < 0 : false;
  const isNumberInvalid =
    isNumberToInvalid || isNumberFromNegative || isNumberToNegative;

  return {
    isNumberToInvalid,
    isNumberFromNegative,
    isNumberToNegative,
    isNumberInvalid,
  };
}
